// Page decoration: the doodle sits fixed behind the page and stays visible while scrolling.
// DoodlePanel keeps the grid/content readable: a blurred, translucent layer behind it that reaches 192px past the content on
// the sides and 128px below it (less on phones) and fades out smoothly, so it never leaves a hard cut through the shapes.
(function () {
  const { Box } = MaterialUI;

  function DoodleBackdrop({ config }) {
    return (
      <Box aria-hidden="true" sx={{ position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none' }}>
        <window.Components.DoodleArt config={config} />
      </Box>
    );
  }

  // Progressive blur: three stacked layers, each blurrier and less wide, all with eased (smoothstep) edge masks. The sharp-to-soft
  // transition then spreads over the whole bleed instead of ending in one visible edge. Top bleed stays small so the layer
  // does not blur the hero and tab bar above the content.
  const EASE = [[0, 0], [0.2, 0.104], [0.4, 0.352], [0.6, 0.648], [0.8, 0.896], [1, 1]];
  const ramp = (dir, from, to = from) => {
    const stops = EASE.map(([t, a]) => `rgba(0,0,0,${a}) ${Math.round(t * from)}px`);
    const rev = EASE.slice().reverse().map(([t, a]) => `rgba(0,0,0,${a}) calc(100% - ${Math.round(t * to)}px)`);
    return `linear-gradient(${dir}, ${stops.join(', ')}, ${rev.join(', ')})`;
  };
  const LAYERS = [
    { blur: 6, side: 192, bottom: 128, top: 32, tint: 0 },
    { blur: 14, side: 144, bottom: 96, top: 24, tint: 38 },
    { blur: 26, side: 96, bottom: 64, top: 16, tint: 24 }
  ];
  const PHONE = 0.125; // bleed on phones is 1/8 of the bottom bleed (16px at the widest layer) so the page never scrolls sideways

  function DoodlePanel({ active, children, sx }) {
    // The bleed reaches past the content; the app shell clips it (overflow: clip) so it adds no scroll area or scrollbar.
    const mask = (x, t, b) => `${ramp('to right', x)}, ${ramp('to bottom', t, b)}`;
    return (
      <Box sx={[{ position: 'relative' }, ...(Array.isArray(sx) ? sx : [sx])]}>
        {active && LAYERS.map((l, i) => (
          <Box
            key={i}
            aria-hidden="true"
            sx={{
              position: 'absolute', pointerEvents: 'none',
              inset: { xs: `-${l.top * PHONE * 4}px -${l.bottom * PHONE}px -${l.bottom * PHONE}px`, md: `-${l.top}px -${l.side}px -${l.bottom}px` },
              backgroundColor: l.tint ? `color-mix(in srgb, var(--m3-surface) ${l.tint}%, transparent)` : 'transparent',
              backdropFilter: `blur(${l.blur}px)`, WebkitBackdropFilter: `blur(${l.blur}px)`,
              maskImage: { xs: mask(l.bottom * PHONE, l.top * PHONE * 4, l.bottom * PHONE), md: mask(l.side, l.top, l.bottom) },
              WebkitMaskImage: { xs: mask(l.bottom * PHONE, l.top * PHONE * 4, l.bottom * PHONE), md: mask(l.side, l.top, l.bottom) },
              maskComposite: 'intersect', WebkitMaskComposite: 'source-in'
            }}
          />
        ))}
        <Box sx={{ position: 'relative' }}>{children}</Box>
      </Box>
    );
  }

  // Wraps a whole page (Apps, Admin console...): doodle behind it, blurred panel around the content, and for admins an edit button.
  function PageDoodle({ target, title, children }) {
    const { DoodleEditButton } = window.Components;
    const { doodle, canEdit } = window.Doodle.useDoodle(target);
    return (
      <Box sx={{ position: 'relative', isolation: 'isolate' }}>
        {doodle && <DoodleBackdrop config={doodle} />}
        {canEdit && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}><DoodleEditButton target={target} title={title} label="Doodle" /></Box>}
        <DoodlePanel active={!!doodle}>{children}</DoodlePanel>
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.PageDoodle = PageDoodle;
  window.Components.DoodleBackdrop = DoodleBackdrop;
  window.Components.DoodlePanel = DoodlePanel;
})();
